import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { Map } from 'immutable';

import classNames from 'classnames';

import { joinString } from '../../../../utils/string';
import If from '../../../helpers/If';
import { GeneralResultRow } from '../GeneralResultRow';

import style from './style.module.css';

/**
 * Stateless component for company result row.
 *
 * @param props {Object}.
 * @param props.company {String} Company information.
 */
const CompanyResultRowComponent = ({ hasAccess, company }) => {
  const statusText = getStatus(company, hasAccess);
  const ownerText = getOwner(company);
  const assignedText = getAssigned(company);
  const type = company.get('type');
  const classColor = type === 'target' ? 'blue' : type === 'buyer' ? 'red' : '';

  return (
    <GeneralResultRow className={style.host} linkTo={`/company/${company.get('id')}/${company.get('type')}`}>
      <div className={style.name}>
        <h4>{company.get('companyName')}</h4>
        <p>{company.get('abbreviatedName')}</p>
      </div>
      <div className={style.location}>
        <div>{company.get('entityType')}</div>
        <div>{company.get('headQuarters')}</div>
      </div>
      <div className={style.website}>{company.get('website')}</div>
      <If value={hasAccess}>
        <div className={style.contact}>
          <h4>{company.get('primaryContact')}</h4>
          <p>{company.get('contactTitle')}</p>
        </div>
      </If>
      <div className={classNames(style.target, classColor)}>{statusText}</div>
      <div className={classNames(style.account, classColor)}>{ownerText}</div>
      <If value={hasAccess}>
        <div className={classNames(style.commentary, classColor)}>{assignedText}</div>
      </If>
      <If value={hasAccess}>
        <div className={style.activity}>
          <div>
            <span className="na-title">Next action:</span>
            <span>{company.get('nextAction')}</span>
          </div>
          <div>
            <span className="na-title">Last action:</span>
            <span>{company.get('lastAction')}</span>
          </div>
        </div>
      </If>
      <div className={style.category}>{company.get('parentCompany')}</div>
    </GeneralResultRow>
  );
};

CompanyResultRowComponent.propTypes = {
  company: PropTypes.instanceOf(Map).isRequired,
};

export const CompanyResultRow = memo(CompanyResultRowComponent);

const getAssigned = company => {
  if (company.get('recordOwnerId')) {
    return company.get('assignedToTarget');
  }

  return null;
};

const getStatus = (company, hasAccess) => {
  const recordOwnerId = company.get('recordOwnerId');
  const buyerType = company.get('buyerType');
  const targetStatus = company
    .get('targetStatus')
    .split('/')
    .map(i => i.trim());
  const buyerStatus = company.get('buyerStatus');

  if (hasAccess) {
    return (
      <div>
        {recordOwnerId ? (
          <div className="text-uppercase target blue">
            TARGET:
            <strong>
              {targetStatus[0]}/ {targetStatus[1]}
            </strong>
          </div>
        ) : null}
        {buyerType ? (
          <div className="text-uppercase buyer red">
            {joinString(' ', buyerType, 'BUYER:')} <strong>{buyerStatus}</strong>
          </div>
        ) : null}
      </div>
    );
  }

  let statuses = null;

  if (recordOwnerId) {
    const statusValue = parseFloat(targetStatus[0]);

    if (statusValue < 2) {
      statuses = (
        <div>
          <div className="text-uppercase target blue">
            <strong>{targetStatus[0]}</strong>
          </div>
          <div className="text-uppercase target blue">
            <strong>{targetStatus[1]}</strong>
          </div>
        </div>
      );
    } else if (statusValue >= 2 && statusValue < 2.99) {
      statuses = <strong>Client No Interest</strong>;
    } else if (statusValue >= 2.99 && statusValue <= 10) {
      statuses = <strong>Research Complete</strong>;
    }
  }

  return statuses;
};

const getOwner = company => {
  if (company.get('recordOwnerId')) {
    return company.get('targetRecordOwner');
  }

  return null;
};
